import React, { useContext, useEffect, useMemo, useState, useRef } from "react";
import SocialLinks from "./SocialLinks";
import PropTypes from "prop-types";
import { NewsAndAuth } from "./NewsAndAuth";
import CoinLogo from "./CoinLogo";
import { ConstantsContext } from "../index";
import { numberToAmericanFormat } from "./functions";
import logo512 from "../assets/img/logo100_100.png";
import ParticlesBg from 'particles-bg';
import NewsBriefTable from "./NewsBriefTable";
import BlogAndArticleSection from "./BlogAndArticleSection";

// Example: pass your real CMD news array here
const news = [];

// NOTE: This component assumes TailwindCSS is available in your project.
// Drop this file into your Next.js app as app/page.tsx (or pages/index.tsx) and export default.
// Replace the TODOs to hook to your real APIs.

export default function CryptoMarketDartsLanding({ onLogin, userDetails, _apiConnect }) {
  // style for the particles background (only top 400px area).
  const particlesBgStyle = {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: 700,          // make this '100%' to cover the whole page
    zIndex: 0,
    pointerEvents: "none" // don't intercept clicks
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 relative overflow-hidden">
      {/* lowest layer */}
      <ParticlesBg
        num={100}
        type="cobweb"
        color="#8FD0F8"
        bg={particlesBgStyle}
      />

      {/* decorative layer above particles */}
      <BackgroundDecor />

      {/* content layer */}
      <Header onLogin={onLogin} />
      <main className="relative z-10">
        <HeroSection onLogin={onLogin} />
        <NewsAndAuth userDetails={userDetails} _apiConnect={_apiConnect} />
        
        <BlogAndArticleSection userDetails={userDetails} _apiConnect={_apiConnect} />

        <NewsBriefTable
          items={news}
          title="Crypto News"
          updatedAgo=""
          initialLimit={3}
        />
        
        <FeatureGrid />
        
        
      </main>
    </div>
  );
}

/* ============================ HEADER ============================ */

function Header({ onLogin }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const popRef = useRef(null);

  // Close on ESC
  useEffect(() => {
    if (!open) return;
    const onEsc = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [open]);

  // Desktop click-away (don’t use a full-screen overlay)
  useEffect(() => {
    if (!open) return;

    // only attach on desktop (Tailwind md breakpoint = 768px)
    const isDesktop = window.matchMedia("(min-width: 768px)").matches;
    if (!isDesktop) return;

    const onDown = (e) => {
      const t = e.target;
      if (popRef.current?.contains(t)) return; // inside popover
      if (btnRef.current?.contains(t)) return; // the "Sign In" button
      setOpen(false);
    };

    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  return (
    <>
      <header className="sticky top-0 z-20 backdrop-blur supports-[backdrop-filter]:bg-slate-950/70 bg-slate-950/90 border-b border-white/5">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LogoMark />
            <span className="font-semibold tracking-tight">CryptoMarketDarts</span>
          </div>

          <nav className="hidden md:flex items-center gap-8 text-sm text-slate-300">
            <a className="hover:text-white transition" href="#news">News</a>
            <a className="hover:text-white transition" href="#performance">Dashboard</a>
            <a className="hover:text-white transition" href="#features">Features</a>
            <a className="hover:text-white transition" href="#video">Intro</a>
          </nav>

          {/* RIGHT: anchor area */}
          <div className="relative flex items-center gap-3">
            <button
              ref={btnRef}
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="text-sm text-slate-300 hover:text-white"
            >
              Sign In
            </button>
            <a
              href="/SignUp"
              className="inline-flex items-center rounded-xl bg-emerald-500 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-emerald-400 transition shadow"
            >
              Register
            </a>

            {/* Desktop popover: absolute under the Sign In button, aligned RIGHT */}
            {open && (
              <div className="hidden md:block absolute right-0 top-full mt-2">
                <div
                  ref={popRef}
                  className="rounded-2xl border border-white/10 bg-slate-950/90 shadow-2xl"
                  style={{ width: 300, maxWidth: 300, padding: 12, boxSizing: "border-box" }}
                  onMouseDown={(e) => e.stopPropagation()} // prevent bubbling to document
                >
                  <SignInCard
                    onLogin={async (creds) => {
                      await onLogin?.(creds);
                      setOpen(false);
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Mobile full-screen modal (overlay only on mobile) */}
      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Sign in"
          className="md:hidden fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          onClick={() => setOpen(false)}
        >
          <div
            className="relative w-full"
            style={{ maxWidth: 300 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="absolute -right-2 -top-2 h-8 w-8 rounded-full bg-white/10 text-white hover:bg-white/20"
              aria-label="Close"
              title="Close"
            >
              ×
            </button>
            <SignInCard
              onLogin={async (creds) => {
                await onLogin?.(creds);
                setOpen(false);
              }}
            />
          </div>
        </div>
      )}
    </>
  );
}


/* ============================ HERO ============================ */
function HeroSection({onLogin}) {
  return (
    <section className="relative isolate">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 sm:py-18">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <p className="mt-4 text-slate-400 max-w-2xl" style={{ fontSize: '120%' }}>
              We built CryptoMarketDarts (CMD) to do what human analysts can’t: watch the crypto markets
              around the clock, adapt instantly, and evolve strategy in real time. It’s not a tool—it’s a live AI
              incubator that feeds our fund with edge.
            </p>

            <p
              className="mt-4 text-slate-400 max-w-2xl"
              style={{ fontSize: '120%' }}
            >
              OpenAI rated our model as being in the{' '}
              <strong className="font-semibold">top 1%</strong>{' '}
              of GPT-based trading models for execution structure, capital allocation logic, and autonomous strategy control.
            </p>

            {/* Sub-titles / highlights */}
            <ul className="mt-5 space-y-2 text-slate-200">
              <li className="flex items-start gap-2">
                <span className="mt-2 h-2 w-2 rounded-full" style={{ backgroundColor: "#10B981" }} />
                <span><strong>AI Analyst Bots</strong> – Born, Trained &amp; Tested In-House</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-2 h-2 w-2 rounded-full" style={{ backgroundColor: "#10B981" }} />
                <span><strong>Signals with Structure</strong> – Powered by Our Dart Indicator</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-2 h-2 w-2 rounded-full" style={{ backgroundColor: "#10B981" }} />
                <span><strong>Emotionless Execution</strong> – Human-in-the-Loop Only Where It Matters</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-2 h-2 w-2 rounded-full" style={{ backgroundColor: "#10B981" }} />
                <span><strong>AI-First Infrastructure</strong> – Scalable, Repeatable, Fast</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-2 h-2 w-2 rounded-full" style={{ backgroundColor: "#10B981" }} />
                <span><strong>Proven Performance</strong> – Early Returns Show Consistent Edge</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-2 h-2 w-2 rounded-full" style={{ backgroundColor: "#10B981" }} />
                <span><strong>Why It Matters:</strong> CMD is not just a tool - it’s a strategy factory</span>
              </li>
            </ul>

          </div>

          <div className="lg:col-span-5 lg:pl-8">
            <VideoSection />
            
          </div>
        </div>
      </div>
    </section>
  );
}



/* ============================ SIGN-IN CARD ============================ */
function SignInCard({onLogin}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      if (typeof onLogin === "function") {
        // Option A: pass { email, password }
        await onLogin({ email, password });

        // If your API expects userEmail/userPassword instead, use:
        // await onLogin({ userEmail: email, userPassword: password });
      } else {
        // Optional fallback
        await new Promise(r => setTimeout(r, 800));
        alert(`Logged in as ${email} (demo)`);
      }
    } finally {
      setLoading(false);
    }
  }


  return (
    <div
      id="signin"
      className="rounded-2xl p-6 shadow-xl"
      style={{
        width: 300,
        boxSizing: "border-box",
        background: "#0B3A53",                 // solid deep blue (no transparency)
        border: "1px solid rgba(255,255,255,0.08)"
        // no backdropFilter here
      }}
    >
      <h3 className="text-lg font-semibold">Sign in</h3>
      <p className="mt-1 text-sm text-slate-400">Welcome back to CryptoMarketDarts</p>
      <form onSubmit={handleSubmit} className="mt-5 space-y-4">
        <div>
          <label className="text-xs text-slate-300">Email</label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="mt-1 w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-400/50"
          />
        </div>
        <div>
          <label className="text-xs text-slate-300">Password</label>
          <div className="relative mt-1">
            <input
              type={showPw ? "text" : "password"}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2 pr-10 text-sm outline-none focus:ring-2 focus:ring-emerald-400/50"
            />
            <button type="button" onClick={() => setShowPw(v=>!v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400 hover:text-slate-200">
              {showPw ? "Hide" : "Show"}
            </button>
          </div>
        </div>
        <button
          type="submit"
          disabled={loading}
          className="w-full rounded-xl bg-emerald-500 py-2 text-sm font-semibold text-slate-950 hover:bg-emerald-400 transition disabled:opacity-60"
        >
          {loading ? "Signing in…" : "Sign In"}
        </button>
        <div className="flex items-center justify-between text-xs">
          <a className="text-slate-400 hover:text-white" href="/ForgotPassword">Forgot password?</a>
          <a className="text-emerald-400 hover:text-emerald-300" href="/SignUp">Create an account</a>
        </div>
      </form>
    </div>
  );
}

SignInCard.propTypes = {
  onLogin: PropTypes.func,
};



/* ============================ VIDEO ============================ */
function VideoSection() {
  return (
    <div className="w-full">
      <div className="relative w-full" style={{ paddingTop: "56.25%" }}> {/* 16:9 ratio */}
        <iframe
          className="absolute top-0 left-0 w-full h-full rounded-xl"
          src="https://www.youtube.com/embed/7WUqrEhjq5I?rel=0"
          title="CryptoMarketDarts Intro"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      </div>
    </div>
  );
}

/* ============================ FEATURES ============================ */
function FeatureGrid() {
  const items = [
    {
      title: "What Makes Us Different",
      desc: "We're not just building a fund - we're building an AI-native strategy engine.",
      icon: <SparkIcon />,
    },
    {
      title: "Discipline",
      desc: "Our system was independently rated by OpenAI as being in the top 1% of GPT-based trading models for execution structure, capital allocation logic, and autonomous strategy control.",
      icon: <BellIcon />,
    },
    {
      title: "Strategy Factory",
      desc: "We’re building thought engines — autonomous, explainable, modular AI agents that scale across portfolios, coins, and fund structures.",
      icon: <GaugeIcon />,
    },
  ];
  return (
    <section id="features" className="relative">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-3 gap-6">
          {items.map((f) => (
            <div key={f.title} className="rounded-2xl border border-white/10 bg-white/5 p-6 hover:bg-white/10 transition">
              <div className="mb-3 h-10 w-10 rounded-xl bg-white/10 flex items-center justify-center">
                {f.icon}
              </div>
              <h3 className="font-semibold">{f.title}</h3>
              <p className="mt-1 text-sm text-slate-300">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}



/* ============================ DECOR + SMALL COMPONENTS ============================ */
function BackgroundDecor() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 select-none">
      {/* Grid */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(16,185,129,0.12),transparent_55%)]" />
      {/* Constellation dots */}
      <svg className="absolute -top-10 right-0 h-[600px] w-[900px] opacity-50" viewBox="0 0 900 600">
        {Array.from({ length: 80 }).map((_, i) => (
          <circle key={i} cx={Math.random()*900} cy={Math.random()*600} r={Math.random()*1.8+0.2} fill="white" opacity={0.25} />
        ))}
      </svg>
      {/* vignette */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-slate-950" />
    </div>
  );
}

function LogoMark({ size = 24, className = "" }) {
  return (
    <img
      src={logo512}
      alt="CryptoMarketDarts logo"
      width={size}
      height={size}
      style={{ width: size, height: size, objectFit: "contain" }}
      className={className}
    />
  );
}


function SparkIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="opacity-90">
      <path d="M12 2l2 6 6 2-6 2-2 6-2-6-6-2 6-2 2-6z" stroke="currentColor" strokeWidth="1.3"/>
    </svg>
  );
}
function BellIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="opacity-90">
      <path d="M18 8a6 6 0 10-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" stroke="currentColor" strokeWidth="1.3"/>
      <path d="M10 19a2 2 0 004 0" stroke="currentColor" strokeWidth="1.3"/>
    </svg>
  );
}
function GaugeIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="opacity-90">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.3"/>
      <path d="M12 12l5-3" stroke="currentColor" strokeWidth="1.3"/>
    </svg>
  );
}
